import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BookOpenText, X, Clapperboard, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api from "@/api";
import { guideKeyForPath, guideSteps, guideTitle } from "@/helpGuides";
import { printStationQR, printAllStationQRs } from "@/guideQrPrint";
import { QrCode } from "lucide-react";

const embedOf = (u) => {
  const yt = (u || "").match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = (u || "").match(/vimeo\.com\/(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(u || "")) return { type: "video", src: u };
  return { type: "link", src: u };
};

export default function HelpGuidePanel({ sectionKey, portal = false }) {
  const { t, lang } = useI18n();
  const loc = useLocation();
  const key = sectionKey || guideKeyForPath(loc.pathname);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [saved, setSaved] = useState("");
  const [defIdx, setDefIdx] = useState(0);
  const [defFailed, setDefFailed] = useState(false);

  useEffect(() => { setOpen(false); }, [key]);
  useEffect(() => { setDefIdx(0); setDefFailed(false); }, [key, lang, open]);
  useEffect(() => {
    if (!open || !key) return;
    if (portal) {
      const v = localStorage.getItem(`cc_guide_video_${key}`) || "";
      setSaved(v); setUrl(v);
    } else {
      api.helpVideos().then((m) => { const v = (m || {})[key] || ""; setSaved(v); setUrl(v); }).catch(() => {});
    }
  }, [open, key, portal]);

  if (!key) return null;
  const steps = guideSteps(key, lang);
  if (!steps) return null;

  const save = async (val) => {
    const v = (val ?? url ?? "").trim();
    if (v && !/^https?:\/\//i.test(v)) { toast.error(t("hgVideoInvalid")); return; }
    try {
      if (portal) {
        if (v) localStorage.setItem(`cc_guide_video_${key}`, v);
        else localStorage.removeItem(`cc_guide_video_${key}`);
      } else {
        await api.helpVideoSet(key, v);
      }
      setSaved(v); setUrl(v);
      toast.success(v ? t("hgVideoSaved") : t("hgVideoRemoved"));
    } catch { toast.error("Errore"); }
  };

  const emb = saved ? embedOf(saved) : null;
  const defSources = lang === "it" ? [`/videos/help/guide_${key}_it.mp4`] : [`/videos/help/guide_${key}_${lang}.mp4`, `/videos/help/guide_${key}_it.mp4`];
  const pdfLang = ["it", "en", "es", "fr", "de"].includes(lang) ? lang : "en";

  return (
    <>
      <button onClick={() => setOpen(true)}
        className={`fixed ${portal ? "bottom-24 md:bottom-6 left-4" : "bottom-4 left-16"} z-[124] flex items-center gap-1.5 bg-white text-[#0d1f3d] border border-[#0d1f3d]/15 rounded-full pl-3 pr-4 py-2 text-xs font-semibold shadow-lg hover:bg-[#f6f2ea] transition-colors`}
        data-testid="help-guide-btn">
        <BookOpenText className="w-4 h-4 text-[#C5A880]" /> {t("hgFabBtn")}
      </button>
      {open && (
        <div className="fixed inset-0 z-[170] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)} data-testid="help-guide-overlay">
          <div onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-lg bg-white text-[#1d1d1f] rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[88vh] overflow-y-auto fade-up"
            data-testid="help-guide-panel">
            <div className="sticky top-0 bg-[#0d1f3d] text-white px-4 py-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#C5A880]">{t("hgTitle")}</div>
                <div className="serif text-lg font-semibold truncate" data-testid="help-guide-title">{guideTitle(key, lang)}</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/15 shrink-0" data-testid="help-guide-close"><X className="w-5 h-5" /></button>
            </div>
            <ol className="p-4 space-y-2.5">
              {steps.map((s, i) => (
                <li key={i} className="flex gap-3 items-start" data-testid={`help-guide-step-${i + 1}`}>
                  <span className="w-6 h-6 rounded-full bg-[#C5A880]/20 text-[#8a6d3b] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-sm leading-snug">{s}</span>
                </li>
              ))}
            </ol>
            <div className="px-4 pb-5">
              <div className="rounded-xl border border-border/70 bg-[#f8f6f1] p-3" data-testid="help-guide-video-box">
                <div className="flex items-center gap-2 text-sm font-semibold mb-1.5">
                  <Clapperboard className="w-4 h-4 text-[#C5A880]" /> {t("hgVideoBox")}
                </div>
                {!emb && !defFailed && (
                  <div className="mb-2.5 rounded-lg overflow-hidden bg-black" data-testid="help-guide-default-video">
                    <video key={defSources[defIdx]} src={defSources[defIdx]} controls playsInline preload="metadata" className="w-full aspect-video"
                      onError={() => (defIdx + 1 < defSources.length ? setDefIdx(defIdx + 1) : setDefFailed(true))} />
                  </div>
                )}
                {emb && (
                  <div className="mb-2.5 rounded-lg overflow-hidden bg-black" data-testid="help-guide-video-player">
                    {emb.type === "iframe" && <iframe src={emb.src} title="guide-video" className="w-full aspect-video" allowFullScreen frameBorder="0" />}
                    {emb.type === "video" && <video src={emb.src} controls playsInline className="w-full aspect-video" />}
                    {emb.type === "link" && (
                      <a href={emb.src} target="_blank" rel="noreferrer" className="block p-3 text-xs text-white underline break-all" data-testid="help-guide-video-link">{emb.src}</a>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground mb-2">{t("hgVideoHint")}</p>
                <div className="flex gap-2">
                  <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-nospell="1"
                    className="flex-1 min-w-0 rounded-lg border border-border px-3 py-2 text-xs bg-white"
                    data-testid="help-guide-video-input" />
                  <button onClick={() => save()} className="px-3 py-2 rounded-lg bg-[#0d1f3d] text-white text-xs font-semibold hover:opacity-90" data-testid="help-guide-video-save">{t("hgVideoSave")}</button>
                  {saved && (
                    <button onClick={() => save("")} className="p-2 rounded-lg border border-border text-muted-foreground hover:text-red-600" title={t("hgVideoRemoveTitle")} data-testid="help-guide-video-remove"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
              <a href={`/docs/ChefControl-Guide-${pdfLang.toUpperCase()}.pdf`} download
                className="mt-3 flex items-center justify-center gap-1.5 text-xs font-semibold text-[#0d1f3d] bg-[#C5A880]/25 rounded-full px-3 py-2 hover:bg-[#C5A880]/40 transition-colors"
                data-testid="help-guide-pdf-btn">
                <BookOpenText className="w-3.5 h-3.5" /> {t("hgPdfBtn")}
              </a>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button onClick={() => printStationQR(key, pdfLang, t("hgQrCardHint"))}
                  className="flex items-center justify-center gap-1.5 text-[11px] font-semibold text-[#0d1f3d] border border-[#0d1f3d]/20 rounded-full px-2 py-2 hover:bg-[#f6f2ea] transition-colors"
                  data-testid="help-guide-qr-btn">
                  <QrCode className="w-3.5 h-3.5 text-[#C5A880]" /> {t("hgQrBtn")}
                </button>
                <button onClick={() => printAllStationQRs(pdfLang, t("hgQrCardHint"))}
                  className="flex items-center justify-center gap-1.5 text-[11px] font-semibold text-[#0d1f3d] border border-[#0d1f3d]/20 rounded-full px-2 py-2 hover:bg-[#f6f2ea] transition-colors"
                  data-testid="help-guide-qr-all-btn">
                  <QrCode className="w-3.5 h-3.5 text-[#C5A880]" /> {t("hgQrAllBtn")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
